import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMsal } from '@azure/msal-react'
import { Loader } from '@/components/common/Loader'
import {
  errorComentarioDe, OID_PROVISIONAL, PREFIJO_PENDIENTE, useComentarios, type ErrorComentario,
} from '@/hooks/useComentarios'
import type { ComentarioCarga } from '@/types'

/** Tope del servidor. Sólo sirve para el contador: la autoridad sigue siendo el backend. */
const MAX_CARACTERES = 1000
/** Mensajes del mismo autor con menos de esto entre sí se pintan como un solo bloque. */
const VENTANA_AGRUPAR_MS = 5 * 60 * 1000
/** A partir de esta fracción del tope el contador aparece; antes sería ruido. */
const UMBRAL_CONTADOR = 0.8

function fecha(iso?: string | null): Date | null {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d
}

function hora(iso?: string | null): string {
  const d = fecha(iso)
  return d ? d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : ''
}

function claveDia(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
}

function iniciales(nombre: string): string {
  const partes = nombre.replace(/@.*$/, '').split(/[\s._-]+/).filter(Boolean)
  const letras = partes.length > 1 ? partes[0][0] + partes[partes.length - 1][0] : (partes[0] ?? '?').slice(0, 2)
  return letras.toUpperCase()
}

interface Burbuja {
  c: ComentarioCarga
  propio: boolean
  autor: string
  primero: boolean   // primero de su bloque: lleva el nombre
  ultimo: boolean    // último de su bloque: lleva la hora y el avatar
  pendiente: boolean
}
interface Dia { clave: string; fecha: Date | null; burbujas: Burbuja[] }

const IconoX = () => (
  <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
    <path d="M5 5l10 10M15 5L5 15" />
  </svg>
)
const IconoEnviar = () => (
  <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M10 16V4M5 9l5-5 5 5" />
  </svg>
)
const IconoChat = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3h11A2.5 2.5 0 0 1 20 5.5v8a2.5 2.5 0 0 1-2.5 2.5H11l-4.5 4v-4h0A2.5 2.5 0 0 1 4 13.5v-8Z" />
    <path d="M8.5 8.5h7M8.5 11.5h4" />
  </svg>
)
const IconoAviso = () => (
  <svg viewBox="0 0 20 20" className="h-4 w-4 shrink-0 mt-px" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M10 3 2 17h16L10 3Z" /><path d="M10 8.5v3.5M10 14.6v.1" />
  </svg>
)

/**
 * El hilo de una carga: conversación entre logística y ventas. Sin el marco del
 * cajón, para poder montarlo dentro de la ficha (pestaña Actividad) o dentro de
 * `ChatCargaDrawer`.
 *
 * Como `CamionDrawer`, en un rechazo **no se pierde lo tecleado**: el texto sólo
 * se vacía cuando el servidor confirma. El envío es optimista (el mensaje
 * aparece al instante, atenuado, y se retira si falla).
 *
 * `onCerrar` presente = lleva cabecera con su botón de cierre (modo cajón).
 * Ausente = modo INCRUSTADO: sin cabecera ni borde, ocupa el alto que le den.
 */
export function ChatCargaPanel({ carga, dark, onCerrar, enfocar = true }: {
  carga: string
  dark: boolean
  onCerrar?: () => void
  /** Enfocar el campo de texto al montar. */
  enfocar?: boolean
}) {
  const { t } = useTranslation()
  const { accounts } = useMsal()
  const yo = accounts[0]
  const miOid = (yo?.idTokenClaims?.oid as string | undefined) ?? yo?.localAccountId
  const { comentarios, isLoading, isError, error: errorLista, enviar } = useComentarios(carga, true)
  const [texto, setTexto] = useState('')
  const [error, setError] = useState<ErrorComentario | null>(null)
  const areaRef = useRef<HTMLTextAreaElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  // ¿El lector está al final del hilo? Con el sondeo entran mensajes solos y
  // sólo se le baja si ya estaba abajo: no se le arranca de leer los antiguos.
  const alFinal = useRef(true)

  // El campo recibe el foco al montar (la entrada animada es del `Drawer`).
  useEffect(() => {
    if (!enfocar) return
    const id = requestAnimationFrame(() => areaRef.current?.focus())
    return () => cancelAnimationFrame(id)
  }, [enfocar])

  // Se baja el scroll DEL HILO, no con `scrollIntoView`: ése desplaza también
  // todos los antepasados con scroll, y en la ficha de la carga (donde el chat va
  // al pie) arrastraba la ficha entera hasta abajo nada más abrirla.
  useEffect(() => {
    const el = scrollRef.current
    if (alFinal.current && el) el.scrollTop = el.scrollHeight
  }, [comentarios.length])

  // El campo crece con lo tecleado hasta un tope; después hace scroll por dentro.
  useLayoutEffect(() => {
    const a = areaRef.current
    if (!a) return
    a.style.height = 'auto'
    a.style.height = `${Math.min(a.scrollHeight, 140)}px`
  }, [texto])

  const dias = useMemo<Dia[]>(() => {
    const res: Dia[] = []
    let previo: Burbuja | null = null
    for (const c of comentarios) {
      const propio = c.usuarioOid === OID_PROVISIONAL || (!!miOid && c.usuarioOid === miOid)
      const autor = propio
        ? t('cargasSalida.comentarios.yo')
        : (c.usuarioNombre || c.usuarioCorreo || t('cargasSalida.comentarios.anonimo'))
      const d = fecha(c.creado)
      const clave = d ? claveDia(d) : 'sin-fecha'
      let dia = res[res.length - 1]
      const cambiaDia = !dia || dia.clave !== clave
      if (cambiaDia) { dia = { clave, fecha: d, burbujas: [] }; res.push(dia) }

      const b: Burbuja = { c, propio, autor, primero: true, ultimo: true, pendiente: c.id.startsWith(PREFIJO_PENDIENTE) }
      const dPrevio = previo ? fecha(previo.c.creado) : null
      const mismoBloque = !cambiaDia && !!previo && previo.propio === propio && previo.autor === autor
        && !!d && !!dPrevio && d.getTime() - dPrevio.getTime() < VENTANA_AGRUPAR_MS
      if (mismoBloque && previo) { b.primero = false; previo.ultimo = false }
      dia.burbujas.push(b)
      previo = b
    }
    return res
  }, [comentarios, miOid, t])

  const etiquetaDia = (d: Date | null): string => {
    if (!d) return ''
    const hoy = new Date()
    const ayer = new Date(); ayer.setDate(hoy.getDate() - 1)
    if (claveDia(d) === claveDia(hoy)) return t('cargasSalida.comentarios.hoy')
    if (claveDia(d) === claveDia(ayer)) return t('cargasSalida.comentarios.ayer')
    return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })
  }

  const enviarMensaje = () => {
    const limpio = texto.trim()
    if (!limpio || enviar.isPending) return
    setError(null)
    alFinal.current = true   // al enviar, el propio mensaje sí se ve
    enviar.mutate(limpio, {
      onSuccess: () => { setTexto(''); areaRef.current?.focus() },
      onError: (e) => setError(errorComentarioDe(e)),
    })
  }

  const msgError = (e: ErrorComentario) =>
    t(`cargasSalida.comentarios.errores.${e.codigo}`, {
      defaultValue: t('cargasSalida.comentarios.errores.error_inesperado'),
      ...(e.valores ?? {}),
    })

  // Paleta: una sola decisión por modo, para que claro y oscuro no diverjan.
  const p = dark
    ? {
        panel: 'bg-slate-950 text-slate-100 border-l border-white/10',
        borde: 'border-white/[0.07]',
        titulo: 'text-white',
        suave: 'text-slate-400',
        tenue: 'text-slate-500',
        boton: 'text-slate-400 hover:text-white hover:bg-white/10',
        ajena: 'bg-slate-800/90 text-slate-100 ring-1 ring-white/[0.06]',
        avatar: 'bg-slate-700 text-slate-200',
        campo: 'bg-slate-900 border-white/10 focus-within:border-sky-400/60 focus-within:ring-sky-400/20',
        area: 'text-slate-100 placeholder:text-slate-500',
        alerta: 'bg-rose-500/10 text-rose-200 ring-1 ring-rose-400/30',
        chip: 'bg-slate-900 text-slate-400 ring-1 ring-white/10',
        halo: 'bg-sky-400/10 text-sky-300 ring-1 ring-sky-400/20',
        kbd: 'bg-white/10 text-slate-300',
        enviarOff: 'bg-slate-800 text-slate-600',
      }
    : {
        panel: 'bg-slate-50 text-slate-900 border-l border-slate-200',
        borde: 'border-slate-200/80',
        titulo: 'text-slate-900',
        suave: 'text-slate-500',
        tenue: 'text-slate-400',
        boton: 'text-slate-500 hover:text-slate-900 hover:bg-slate-200/70',
        ajena: 'bg-white text-slate-800 ring-1 ring-slate-200 shadow-sm',
        avatar: 'bg-slate-200 text-slate-600',
        campo: 'bg-white border-slate-200 focus-within:border-sky-500/70 focus-within:ring-sky-500/20',
        area: 'text-slate-900 placeholder:text-slate-400',
        alerta: 'bg-rose-50 text-rose-700 ring-1 ring-rose-200',
        chip: 'bg-white text-slate-500 ring-1 ring-slate-200',
        halo: 'bg-sky-50 text-sky-600 ring-1 ring-sky-200',
        kbd: 'bg-slate-200/80 text-slate-600',
        enviarOff: 'bg-slate-200 text-slate-400',
      }
  const propia = dark
    ? 'bg-gradient-to-br from-sky-500 to-sky-600 text-white shadow-lg shadow-sky-900/30'
    : 'bg-gradient-to-br from-sky-500 to-sky-600 text-white shadow-md shadow-sky-500/20'

  const n = texto.length
  const verContador = n >= MAX_CARACTERES * UMBRAL_CONTADOR
  const excedido = n > MAX_CARACTERES
  const puedeEnviar = !!texto.trim() && !enviar.isPending

  return (
      <div className={`relative w-full h-full min-h-0 flex flex-col ${onCerrar ? p.panel : p.panel.replace(/ border-l border-\S+/, '')}`}>
        {onCerrar && (
        <header className={`flex items-center gap-3 px-5 py-4 border-b ${p.borde} ${dark ? 'bg-slate-900/60' : 'bg-white'}`}>
          <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ${p.halo}`}>
            <IconoChat className="h-[18px] w-[18px]" />
          </span>
          <div className="min-w-0 flex-1">
            <p className={`text-[10px] font-semibold uppercase tracking-[0.14em] ${p.tenue}`}>
              {t('cargasSalida.comentarios.etiqueta')}
            </p>
            <h2 className={`truncate text-[15px] font-black leading-tight tracking-tight tabular-nums ${p.titulo}`} title={carga}>
              {carga}
            </h2>
          </div>
          {!isLoading && !isError && comentarios.length > 0 && (
            <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-semibold ${p.chip}`}>
              {t('cargasSalida.comentarios.resumen', { count: comentarios.length })}
            </span>
          )}
          <button
            type="button" onClick={onCerrar}
            aria-label={t('cargasSalida.comentarios.cerrar')} title={t('cargasSalida.comentarios.cerrar')}
            className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg transition-colors ${p.boton}`}
          >
            <IconoX />
          </button>
        </header>
        )}

        <div
          ref={scrollRef}
          onScroll={(e) => {
            const el = e.currentTarget
            alFinal.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80
          }}
          className="flex-1 overflow-y-auto scroll-thin px-4 py-5"
        >
          {isLoading ? (
            <div className="grid h-full place-items-center"><Loader label={t('cargasSalida.comentarios.cargando')} /></div>
          ) : isError && errorLista ? (
            <div role="alert" className={`flex items-start gap-2.5 rounded-xl px-3.5 py-3 text-[12px] leading-relaxed ${p.alerta}`}>
              <IconoAviso />
              <span>{msgError(errorLista)}</span>
            </div>
          ) : comentarios.length === 0 ? (
            <div className="grid h-full place-items-center px-6">
              <div className="flex max-w-[260px] flex-col items-center text-center">
                <span className={`mb-4 grid h-14 w-14 place-items-center rounded-2xl ${p.halo}`}>
                  <IconoChat className="h-7 w-7" />
                </span>
                <p className={`text-[14px] font-bold tracking-tight ${p.titulo}`}>
                  {t('cargasSalida.comentarios.vacioTitulo')}
                </p>
                <p className={`mt-1.5 text-[12px] leading-relaxed ${p.suave}`}>
                  {t('cargasSalida.comentarios.vacio')}
                </p>
              </div>
            </div>
          ) : (
            <div>
              {dias.map((dia) => (
                <section key={dia.clave}>
                  {dia.fecha && (
                    <div className="my-3 flex items-center gap-3">
                      <span className={`h-px flex-1 ${dark ? 'bg-white/[0.07]' : 'bg-slate-200'}`} />
                      <span className={`text-[10px] font-semibold uppercase tracking-[0.12em] ${p.tenue}`}>
                        {etiquetaDia(dia.fecha)}
                      </span>
                      <span className={`h-px flex-1 ${dark ? 'bg-white/[0.07]' : 'bg-slate-200'}`} />
                    </div>
                  )}
                  {dia.burbujas.map(({ c, propio, autor, primero, ultimo, pendiente }) => (
                    <div
                      key={c.id}
                      className={`flex items-end gap-2 ${propio ? 'flex-row-reverse' : ''} ${primero ? 'mt-3.5' : 'mt-0.5'}`}
                    >
                      {!propio && (
                        <span
                          className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-[10px] font-bold ${ultimo ? p.avatar : 'invisible'}`}
                          aria-hidden
                        >
                          {iniciales(autor)}
                        </span>
                      )}
                      <div className={`flex min-w-0 max-w-[80%] flex-col ${propio ? 'items-end' : 'items-start'}`}>
                        {primero && !propio && (
                          <span className={`mb-1 ml-1 max-w-full truncate text-[11px] font-semibold ${p.suave}`}>{autor}</span>
                        )}
                        <div
                          className={`whitespace-pre-wrap break-words px-3.5 py-2 text-[13px] leading-snug transition-opacity ${
                            propio ? propia : p.ajena
                          } ${pendiente ? 'opacity-60' : ''} ${
                            propio
                              ? `rounded-2xl ${primero ? '' : 'rounded-tr-md'} ${ultimo ? 'rounded-br-sm' : 'rounded-br-md'}`
                              : `rounded-2xl ${primero ? '' : 'rounded-tl-md'} ${ultimo ? 'rounded-bl-sm' : 'rounded-bl-md'}`
                          }`}
                        >
                          {c.texto}
                        </div>
                        {ultimo && (
                          <span className={`mt-1 px-1 text-[10px] tabular-nums ${p.tenue}`}>
                            {pendiente ? '…' : hora(c.creado)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </section>
              ))}
            </div>
          )}
        </div>

        <footer className={`border-t px-4 pb-4 pt-3 ${p.borde} ${dark ? 'bg-slate-900/60' : 'bg-white'}`}>
          {error && (
            <div role="alert" className={`mb-2.5 flex items-start gap-2.5 rounded-xl px-3.5 py-2.5 text-[12px] leading-relaxed ${p.alerta}`}>
              <IconoAviso />
              <span>{msgError(error)}</span>
            </div>
          )}
          <div className={`flex items-end gap-2 rounded-2xl border py-1.5 pl-3.5 pr-1.5 transition-shadow focus-within:ring-4 ${p.campo}`}>
            <textarea
              ref={areaRef}
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); enviarMensaje() }
              }}
              rows={1}
              placeholder={t('cargasSalida.comentarios.placeholder')}
              aria-label={t('cargasSalida.comentarios.placeholder')}
              className={`min-w-0 flex-1 resize-none bg-transparent py-1.5 text-[13px] leading-snug outline-none ${p.area}`}
            />
            <button
              type="button"
              disabled={!puedeEnviar}
              onClick={enviarMensaje}
              aria-label={t('cargasSalida.comentarios.enviar')} title={t('cargasSalida.comentarios.enviar')}
              className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl transition-all ${
                puedeEnviar
                  ? 'bg-sky-500 text-white shadow-md shadow-sky-500/30 hover:bg-sky-400 active:scale-95'
                  : p.enviarOff
              }`}
            >
              {enviar.isPending ? <Loader size={9} /> : <IconoEnviar />}
            </button>
          </div>
          <div className={`mt-2 flex items-center justify-between gap-3 px-1 text-[10px] ${p.tenue}`}>
            <span className="truncate">
              <kbd className={`rounded px-1.5 py-0.5 font-sans font-semibold ${p.kbd}`}>Ctrl</kbd>
              {' + '}
              <kbd className={`rounded px-1.5 py-0.5 font-sans font-semibold ${p.kbd}`}>Enter</kbd>
              {' '}{t('cargasSalida.comentarios.atajo')}
            </span>
            {verContador && (
              <span className={`shrink-0 tabular-nums font-semibold ${excedido ? (dark ? 'text-rose-300' : 'text-rose-600') : ''}`}>
                {t('cargasSalida.comentarios.caracteres', { n, max: MAX_CARACTERES })}
              </span>
            )}
          </div>
        </footer>
      </div>
  )
}
